/**
 * /ambassadors/apply — Public self-apply form for the ambassador program.
 *
 * Posts to `POST /api/ambassador/apply` (public, no auth). Backend is
 * idempotent on (email, pending) so re-submits return the same
 * application_id rather than duplicating rows. On success we render
 * a "thanks, we'll be in touch within 48h" state instead of a toast
 * — partly because we're often capturing this on mobile in the
 * middle of a video shoot and a persistent confirmation is friendlier
 * than a 4-second toast that disappears.
 *
 * Fully localised via `ambassadorsApply.*` keys (en/es/fr/pt).
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Loader2, Heart } from "lucide-react";
import { submitAmbassadorApplication } from "../lib/api";

const AmbassadorsApply = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [handle, setHandle] = useState("");
  const [social, setSocial] = useState("");
  const [audience, setAudience] = useState("");
  const [why, setWhy] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      const data = await submitAmbassadorApplication({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        handle: handle.trim() || null,
        social_url: social.trim() || null,
        audience_size: audience.trim() || null,
        why: why.trim() || null,
      });
      setResult(data);
      if (data?.already_pending) {
        toast.success(t("ambassadorsApply.errorAlreadyPending"));
      }
    } catch {
      toast.error(t("ambassadorsApply.errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FDFBF7] text-[#2C2123]"
      data-testid="ambassadors-apply-page"
    >
      <header className="px-5 sm:px-10 pt-6 pb-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <span className="font-serif italic text-xl tracking-tight">
            TruBeauté
          </span>
        </Link>
        <Link
          to="/ambassadors"
          className="text-xs text-[#5C4B51] hover:text-[#2C2123] underline"
        >
          ← {t("ambassadorsPublic.hero.eyebrow")}
        </Link>
      </header>

      <main className="px-5 sm:px-10 max-w-2xl mx-auto pt-10 sm:pt-14 pb-20">
        {result?.ok && !result.already_pending ? (
          <section
            className="bg-white border border-[#EADDD7] rounded-3xl p-8 sm:p-10 text-center shadow-[0_20px_50px_-18px_rgba(155,106,108,0.20)]"
            data-testid="ambassadors-apply-success"
          >
            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center mx-auto mb-4">
              <Heart className="h-6 w-6 text-white" fill="currentColor" />
            </div>
            <h1 className="font-serif italic text-3xl sm:text-4xl mb-3">
              {t("ambassadorsApply.successTitle")}
            </h1>
            <p className="text-base text-[#5C4B51] mb-6 leading-relaxed">
              {t("ambassadorsApply.successBody")}
            </p>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white px-5 py-2.5 rounded-full hover:bg-[#805557] transition-colors"
              data-testid="ambassadors-apply-success-cta"
            >
              {t("ambassadorsApply.successCta")}
              <ArrowRight className="h-4 w-4" />
            </button>
          </section>
        ) : (
          <>
            <h1
              className="font-serif italic text-4xl sm:text-5xl leading-tight mb-2"
              data-testid="ambassadors-apply-headline"
            >
              {t("ambassadorsApply.title")}
            </h1>
            <p className="text-sm text-[#8A7D82] mb-7">
              {t("ambassadorsApply.subtitle")}
            </p>

            <form
              onSubmit={submit}
              className="bg-white border border-[#EADDD7] rounded-3xl p-6 sm:p-7 space-y-4 shadow-[0_10px_30px_-12px_rgba(155,106,108,0.10)]"
            >
              <Field label={t("ambassadorsApply.nameLabel")} required>
                <input
                  required
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("ambassadorsApply.namePlaceholder")}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none"
                  data-testid="ambassadors-apply-name"
                />
              </Field>

              <Field label={t("ambassadorsApply.emailLabel")} required>
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("ambassadorsApply.emailPlaceholder")}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none"
                  data-testid="ambassadors-apply-email"
                />
              </Field>

              <Field
                label={t("ambassadorsApply.handleLabel")}
                help={t("ambassadorsApply.handleHelp")}
              >
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7D82]">
                    @
                  </span>
                  <input
                    type="text"
                    value={handle}
                    onChange={(e) =>
                      setHandle(
                        e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, "")
                          .slice(0, 24),
                      )
                    }
                    className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none"
                    placeholder="alex"
                    data-testid="ambassadors-apply-handle"
                  />
                </div>
              </Field>

              <Field label={t("ambassadorsApply.socialLabel")}>
                <input
                  type="url"
                  value={social}
                  onChange={(e) => setSocial(e.target.value)}
                  placeholder={t("ambassadorsApply.socialPlaceholder")}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none"
                  data-testid="ambassadors-apply-social"
                />
              </Field>

              <Field label={t("ambassadorsApply.audienceLabel")}>
                <input
                  type="text"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder={t("ambassadorsApply.audiencePlaceholder")}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none"
                  data-testid="ambassadors-apply-audience"
                />
              </Field>

              <Field label={t("ambassadorsApply.whyLabel")}>
                <textarea
                  rows={4}
                  value={why}
                  onChange={(e) => setWhy(e.target.value)}
                  placeholder={t("ambassadorsApply.whyPlaceholder")}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none resize-none"
                  data-testid="ambassadors-apply-why"
                />
              </Field>

              <button
                type="submit"
                disabled={submitting || !name.trim() || !email.trim()}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white px-5 py-3 rounded-full hover:bg-[#805557] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                data-testid="ambassadors-apply-submit"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Heart className="h-4 w-4" fill="currentColor" />
                )}
                {submitting
                  ? t("ambassadorsApply.submitting")
                  : t("ambassadorsApply.submit")}
                <ArrowRight className="h-4 w-4" />
              </button>

              {/* Inline consent — clicking the submit button is the
                  affirmative act. The /ambassadors/apply form is public
                  (no account yet), so this is the only legal touch
                  before approval mints the actual account invite.
                  Once approved + the ambassador clicks the email claim
                  link, the standard /login flow's full inline-consent
                  pattern applies again. */}
              <p
                className="text-[11px] text-[#8A7D82] leading-relaxed text-center mt-3"
                data-testid="ambassadors-apply-legal"
              >
                <Trans
                  i18nKey="ambassadorsApply.legalInline"
                  components={{
                    terms: (
                      <Link
                        to="/terms"
                        target="_blank"
                        className="text-[#9B6A6C] underline hover:text-[#805557]"
                      />
                    ),
                    privacy: (
                      <Link
                        to="/privacy"
                        target="_blank"
                        className="text-[#9B6A6C] underline hover:text-[#805557]"
                      />
                    ),
                  }}
                />
              </p>
            </form>
          </>
        )}
      </main>
    </div>
  );
};

const Field = ({ label, required, help, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-wider text-[#5C4B51] mb-1.5">
      {label}
      {required && <span className="text-[#9B6A6C] ml-1">*</span>}
    </span>
    {children}
    {help && (
      <span className="block text-[11px] text-[#8A7D82] mt-1">{help}</span>
    )}
  </label>
);

export default AmbassadorsApply;
